import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import FieldErrorText, {
  fieldErrorHelperText,
} from '@/components/FieldErrorText/FieldErrorText';
import {
  emptyAcknowledgementDelivery,
  emptyDailyReportDelivery,
  previewAcknowledgementFileName,
  previewDailyReportFileName,
} from './sftpDestinations';

const rowStyle = {
  border: '1px solid #e0e0e0',
  borderRadius: 1,
  px: 2,
  pt: 1,
  pb: 2,
  mb: 2,
};

const fileHeadingStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1,
  mt: 1,
};

export default function SftpDestinationRow({
  destination,
  index,
  sftpConnections,
  isDisabled,
  generateAcknowledgementFile,
  generateDailyReport,
  fileExtension,
  dateFormat,
  onChange,
  onRemove,
  getFieldErrors,
}) {
  const { t } = useTranslation();
  const path = `sftp_destinations[${index}]`;
  const { acknowledgement, daily_report: dailyReport } = destination;
  const preview = {
    dateFormat,
    fileExtension,
    originalFileLabel: t('dataMappings.sftpDestinations.originalFileName'),
  };

  const update = (changes, changedField) =>
    onChange({ ...destination, ...changes }, `${path}.${changedField}`);

  const updateAcknowledgement = (field, value) =>
    update(
      { acknowledgement: { ...acknowledgement, [field]: value } },
      `acknowledgement.${field}`,
    );

  const updateDailyReport = (field, value) =>
    update(
      { daily_report: { ...dailyReport, [field]: value } },
      `daily_report.${field}`,
    );

  const errorsFor = (field) => getFieldErrors(`${path}.${field}`);

  const textField = (labelKey, value, field, onFieldChange) => (
    <CustomInput
      type='text'
      labelText={t(labelKey)}
      disabled={isDisabled}
      value={value}
      onChange={(e) => onFieldChange(e.target.value)}
      formControlProps={{ fullWidth: true }}
      error={errorsFor(field).length > 0}
      inputProps={{ helperText: fieldErrorHelperText(errorsFor(field)) }}
    />
  );

  const fileToggle = (labelKey, checked, onToggle, isGenerated) => (
    <Box sx={fileHeadingStyle}>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            disabled={isDisabled}
            onChange={(e) => onToggle(e.target.checked)}
          />
        }
        label={t(labelKey)}
      />
      {checked && !isGenerated && (
        <Typography variant='caption' color='text.secondary'>
          {t('dataMappings.sftpDestinations.notGenerated')}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      sx={rowStyle}
      role='group'
      aria-label={t('dataMappings.sftpDestinations.rowLabel', {
        number: index + 1,
      })}>
      <GridContainer alignItems='center'>
        <GridItem xs={10} md={6}>
          <FormControl
            variant='standard'
            fullWidth
            error={errorsFor('client_sftp_connection_id').length > 0}>
            <InputLabel id={`sftp-destination-connection-${index}`}>
              {t('dataMappings.sftpDestinations.connection')}
            </InputLabel>
            <Select
              labelId={`sftp-destination-connection-${index}`}
              value={destination.client_sftp_connection_id}
              disabled={isDisabled}
              onChange={(e) =>
                update(
                  { client_sftp_connection_id: e.target.value },
                  'client_sftp_connection_id',
                )
              }>
              {sftpConnections?.map((connection) => (
                <MenuItem key={connection.id} value={connection.id}>
                  {connection.name || connection.host}
                </MenuItem>
              ))}
            </Select>
            <FieldErrorText messages={errorsFor('client_sftp_connection_id')} />
          </FormControl>
        </GridItem>
        <GridItem xs={2} md={6} style={{ textAlign: 'right' }}>
          <Tooltip title={t('dataMappings.sftpDestinations.remove')}>
            <span>
              <IconButton
                size='small'
                aria-label={t('dataMappings.sftpDestinations.remove')}
                onClick={onRemove}
                disabled={isDisabled}>
                <DeleteOutlineIcon
                  fontSize='small'
                  style={{ color: '#f44336' }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </GridItem>
      </GridContainer>

      {fileToggle(
        'dataMappings.sftpDestinations.sendAcknowledgement',
        !!acknowledgement,
        (checked) =>
          update(
            {
              acknowledgement: checked ? emptyAcknowledgementDelivery() : null,
            },
            'acknowledgement',
          ),
        generateAcknowledgementFile,
      )}
      {acknowledgement && (
        <GridContainer alignItems='center'>
          <GridItem xs={12} md={4}>
            {textField(
              'dataMappings.sftpDestinations.folder',
              acknowledgement.file_path,
              'acknowledgement.file_path',
              (value) => updateAcknowledgement('file_path', value),
            )}
          </GridItem>
          <GridItem xs={12} md={4}>
            {textField(
              'dataMappings.sftpDestinations.acknowledgementPrefix',
              acknowledgement.file_name,
              'acknowledgement.file_name',
              (value) => updateAcknowledgement('file_name', value),
            )}
          </GridItem>
          <GridItem xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledgement.use_original_filename}
                  disabled={isDisabled}
                  onChange={(e) =>
                    updateAcknowledgement(
                      'use_original_filename',
                      e.target.checked,
                    )
                  }
                />
              }
              label={t('dataMappings.sftpDestinations.useOriginalFilename')}
            />
          </GridItem>
          <GridItem xs={12}>
            <FormHelperText>
              {t('dataMappings.sftpDestinations.preview', {
                fileName: previewAcknowledgementFileName(
                  acknowledgement,
                  preview,
                ),
              })}
            </FormHelperText>
          </GridItem>
        </GridContainer>
      )}

      {fileToggle(
        'dataMappings.sftpDestinations.sendDailyReport',
        !!dailyReport,
        (checked) =>
          update(
            { daily_report: checked ? emptyDailyReportDelivery() : null },
            'daily_report',
          ),
        generateDailyReport,
      )}
      {dailyReport && (
        <GridContainer>
          <GridItem xs={12} md={6}>
            {textField(
              'dataMappings.sftpDestinations.folder',
              dailyReport.file_location,
              'daily_report.file_location',
              (value) => updateDailyReport('file_location', value),
            )}
          </GridItem>
          <GridItem xs={12} md={6}>
            {textField(
              'dataMappings.sftpDestinations.dailyReportName',
              dailyReport.file_name,
              'daily_report.file_name',
              (value) => updateDailyReport('file_name', value),
            )}
          </GridItem>
          <GridItem xs={12}>
            <FormHelperText>
              {t('dataMappings.sftpDestinations.preview', {
                fileName: previewDailyReportFileName(dailyReport, preview),
              })}
            </FormHelperText>
          </GridItem>
        </GridContainer>
      )}

      <FieldErrorText messages={getFieldErrors(path)} />
    </Box>
  );
}

SftpDestinationRow.propTypes = {
  destination: PropTypes.shape({
    client_sftp_connection_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    acknowledgement: PropTypes.object,
    daily_report: PropTypes.object,
  }).isRequired,
  index: PropTypes.number.isRequired,
  sftpConnections: PropTypes.array,
  isDisabled: PropTypes.bool,
  generateAcknowledgementFile: PropTypes.bool,
  generateDailyReport: PropTypes.bool,
  fileExtension: PropTypes.string,
  dateFormat: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  getFieldErrors: PropTypes.func.isRequired,
};
